import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Table, Spinner } from 'react-bootstrap';
import { FaSearch, FaWindowClose, FaKey } from 'react-icons/fa';
import { toast } from 'react-toastify';
import moment from 'moment';
import {
  listarDebitosIPTU,
  buscarDebitosIPTU,
  prepararLancamentoIPTU,
} from '~/Services/GestaoIPTUService';
import { extrairMensagemErro } from '~/Utils/Helpers';
import ModalDadosChave from './ModalDadosChave';
import ModalAvisosIPTU from './ModalAvisosIPTU';
import ModalRevisaoLancamentoIPTU from './ModalRevisaoLancamentoIPTU';

// D4 — Lançamento de Débitos IPTU
// Espelha uDebitosIPTU.dfm — Notebook1.nbBuscaDebito (tela 1):
//   • Painel filtros: label "Vencimento Boleto (Site Prefeitura):" + datas
//   • Botões "Buscar Débitos de IPTU" / "Lançar Chave"
//   • Grid resultado (9 colunas)
// A tela 2 (Notebook1.nbLancaChave / RxDBGrid2) virou ModalRevisaoLancamentoIPTU.
const ModalDebitosIPTU = ({ show, onHide, codigosImoveis, cidadeSelecionada }) => {
  const [vencInicial, setVencInicial] = useState(
    moment().startOf('month').format('YYYY-MM-DD')
  );
  const [vencFinal, setVencFinal] = useState(
    moment().endOf('month').format('YYYY-MM-DD')
  );
  const [carregando, setCarregando] = useState(false);
  const [resultados, setResultados] = useState([]);

  const [mostraModalParametros, setMostraModalParametros] = useState(false);
  const [preparando, setPreparando] = useState(false);
  const [preparado, setPreparado] = useState(null);
  const [mostraModalRevisao, setMostraModalRevisao] = useState(false);
  const [avisosPendentes, setAvisosPendentes] = useState([]);
  const [acaoPosAvisos, setAcaoPosAvisos] = useState(null);

  const lista = Array.isArray(codigosImoveis) ? codigosImoveis : [];
  const listaKey = lista.join(',');

  const fecharLimpando = () => {
    setVencInicial(moment().startOf('month').format('YYYY-MM-DD'));
    setVencFinal(moment().endOf('month').format('YYYY-MM-DD'));
    setResultados([]);
    setPreparado(null);
    setMostraModalRevisao(false);
    setMostraModalParametros(false);
    setAvisosPendentes([]);
    setAcaoPosAvisos(null);
    onHide();
  };

  // Auto-listagem ao abrir o modal — espelha uDebitosIPTU.pas:FormShow.
  // Roda o SELECT em Remessa/Retorno/Contas_aPagar pros imóveis filtrados
  // na tela pai, sem enviar remessa à JPlan. Habilita "Lançar Chave" se
  // houver algum 'RETORNO OK' (réplica AtivaLancaChave).
  // Recarrega a listagem read-only (sem JPlan, sem confirmação) —
  // usada tanto pelo useEffect de abertura quanto pelo onLancado depois
  // que o usuário confirma o lançamento na 2ª tela.
  const recarregarListagem = async (codigos) => {
    if (!codigos || codigos.length === 0) {
      setResultados([]);
      return;
    }
    setCarregando(true);
    try {
      const resposta = await listarDebitosIPTU({ codigos });
      setResultados((resposta && resposta.lista) || []);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
      setResultados([]);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (!show) return undefined;
    const codigos = listaKey ? listaKey.split(',').map(Number) : [];
    let ativo = true;
    if (codigos.length === 0) {
      setResultados([]);
      return undefined;
    }
    setCarregando(true);
    listarDebitosIPTU({ codigos })
      .then((resposta) => {
        if (!ativo) return;
        setResultados((resposta && resposta.lista) || []);
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
        setResultados([]);
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => { ativo = false; };
  }, [show, listaKey]);

  // Habilita "Lançar Chave" só quando há débito com RETORNO OK pendente de
  // lançamento — réplica fiel de uDebitosIPTU.pas:148-169 (AtivaLancaChave).
  const temRetornoOk = resultados.some((d) => d.status === 'RETORNO OK');

  const handleBuscar = async () => {
    if (!vencInicial) {
      toast.warning('Vencimento inicial deve ser informado.');
      return;
    }
    if (!vencFinal) {
      toast.warning('Vencimento final deve ser informado.');
      return;
    }
    const dIni = moment(vencInicial, 'YYYY-MM-DD');
    const dFim = moment(vencFinal, 'YYYY-MM-DD');
    if (!dIni.isValid() || !dFim.isValid()) {
      toast.warning('Datas inválidas.');
      return;
    }
    if (dIni.isAfter(dFim)) {
      toast.warning('Vencimento inicial deve ser menor ou igual o vencimento final.');
      return;
    }
    if (!window.confirm('Deseja efetuar a busca dos débitos de IPTU?')) return;

    setCarregando(true);
    try {
      const resposta = await buscarDebitosIPTU({
        codigos: lista,
        vencimento_inicial: vencInicial,
        vencimento_final: vencFinal,
      });
      const novosResultados = (resposta && resposta.lista) || [];
      const avisos = (resposta && resposta.avisos) || [];
      setResultados(novosResultados);
      avisos.forEach((aviso) => toast.warning(aviso));
      // Réplica uDebitosIPTU.pas:670 — MessageDlg('Remessa enviada com sucesso').
      toast.success('Remessa enviada com sucesso');
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setCarregando(false);
    }
  };

  const fmtMoeda = (v) =>
    v == null
      ? '—'
      : Number(v).toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

  const fmtData = (v) => (v ? moment(v).format('DD/MM/YYYY') : '—');

  const codigosRetornoOk = resultados
    .filter((d) => d.status === 'RETORNO OK')
    .map((d) => d.codigo_ativo);

  // Abre o modal "Dados Complementar para Gerar Chave" — espelha o reuso
  // do fExcelGestaoIPTU em uDebitosIPTU.pas:712-794.
  const handleAbrirParametros = () => {
    if (codigosRetornoOk.length === 0) {
      toast.warning('Nenhum débito com status "RETORNO OK" disponível para lançar.');
      return;
    }
    setMostraModalParametros(true);
  };

  // Preparar (sem gravar): backend monta despesas + token, abrimos a 2ª tela.
  const handleConfirmarParametros = async (parametros) => {
    setMostraModalParametros(false);
    if (codigosRetornoOk.length === 0) {
      toast.warning('Nenhum débito com status "RETORNO OK" disponível para lançar.');
      return;
    }

    setPreparando(true);
    try {
      const resp = await prepararLancamentoIPTU({
        codigos: codigosRetornoOk,
        despesa: parametros.despesa,
        vencimento: parametros.vencimento,
        pagar_em: parametros.pagarEm,
        // Apuração não capturada no D4 (mApuracao desabilitado no Delphi).
        apuracao: null,
        tipo_documento: parametros.tipoDocumento,
        // Modalidade é derivada do código de barras no BE (RN-D4-19), não
        // escolhida pelo usuário. Envia vazio.
        modalidade: '',
        vencimento_do_boleto: parametros.vencimentoDoBoleto,
      });
      const avisos = (resp && resp.avisos) || [];
      const despesas = (resp && resp.despesas) || [];
      const token = resp && resp.token;
      const podeAbrirRevisao = token && despesas.length > 0;
      const proximo = () => {
        if (podeAbrirRevisao) {
          setPreparado({ token, despesas });
          setMostraModalRevisao(true);
        } else {
          toast.error('Nenhuma despesa pôde ser preparada para lançamento.');
        }
      };
      if (avisos.length > 0) {
        // Modal blocking — só fecha quando o usuário clica OK
        // (replica MessageDlg do uDebitosIPTU.pas).
        setAvisosPendentes(avisos);
        setAcaoPosAvisos(() => proximo);
      } else {
        proximo();
      }
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setPreparando(false);
    }
  };

  const handleOkAvisos = () => {
    const continuar = acaoPosAvisos;
    setAvisosPendentes([]);
    setAcaoPosAvisos(null);
    if (typeof continuar === 'function') continuar();
  };

  // Após "Lançar" efetivado: fecha revisão e recarrega grid read-only
  // (status muda pra CHAVE LANÇADA) — replica uDebitosIPTU.pas:413-417.
  // NÃO chama handleBuscar (que dispara JPlan + confirmação): só re-lista.
  const handleLancado = async () => {
    setMostraModalRevisao(false);
    setPreparado(null);
    const codigos = listaKey ? listaKey.split(',').map(Number) : [];
    await recarregarListagem(codigos);
  };

  return (
    <>
      <Modal show={show} onHide={fecharLimpando} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>Débitos de IPTU</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Painel de filtros — espelha GroupBox1 (uDebitosIPTU.dfm:39-309). */}
          <div className="border rounded p-2 mb-3 bg-light">
            <Row className="align-items-end">
              <Col xs={12} sm={5} className="mb-2">
                <Form.Label style={{ fontSize: 12, fontWeight: 'bold', color: '#001f5b' }}>
                  Vencimento Boleto (Site Prefeitura):
                </Form.Label>
                <Row noGutters className="align-items-center">
                  <Col xs={5}>
                    <Form.Control
                      type="date"
                      value={vencInicial}
                      onChange={(e) => setVencInicial(e.target.value)}
                      disabled={carregando || preparando}
                    />
                  </Col>
                  <Col xs={2} className="text-center" style={{ color: '#001f5b', fontWeight: 'bold', fontSize: 12 }}>
                    até
                  </Col>
                  <Col xs={5}>
                    <Form.Control
                      type="date"
                      value={vencFinal}
                      onChange={(e) => setVencFinal(e.target.value)}
                      disabled={carregando || preparando}
                    />
                  </Col>
                </Row>
              </Col>
              <Col xs={12} sm={7} className="mb-2 d-flex" style={{ gap: 8 }}>
                <Button
                  variant="primary"
                  onClick={handleBuscar}
                  disabled={carregando || preparando}
                  className="d-inline-flex align-items-center"
                >
                  {carregando ? (
                    <Spinner animation="border" size="sm" className="mr-1" />
                  ) : (
                    <FaSearch className="mr-1" />
                  )}
                  Buscar Débitos de IPTU
                </Button>
                <Button
                  variant="warning"
                  onClick={handleAbrirParametros}
                  disabled={carregando || preparando || !temRetornoOk}
                  className="d-inline-flex align-items-center"
                  title={
                    !temRetornoOk
                      ? 'Nenhum débito com status "RETORNO OK" disponível'
                      : 'Abre a tela de revisão antes do lançamento'
                  }
                >
                  {preparando ? (
                    <Spinner animation="border" size="sm" className="mr-1" />
                  ) : (
                    <FaKey className="mr-1" />
                  )}
                  Lançar Chave
                </Button>
              </Col>
            </Row>
            <div style={{ fontSize: 12, color: '#6c757d' }}>
              <strong>{lista.length}</strong> imóvel(is) filtrado(s) na grid principal.
              {cidadeSelecionada && (
                <>
                  {' '}Cidade do filtro: <code>{cidadeSelecionada}</code>.
                </>
              )}
            </div>
          </div>

          {/* Grid de resultados — espelha RxDBGrid1 (uDebitosIPTU.dfm:311-431).
              9 colunas: Empreendimento, Código, QD/LT, Vencimento, Valor Original,
              Juros/Multa, Valor Total, Status, Chave. */}
          <div style={{ overflowX: 'auto' }}>
            <Table bordered hover size="sm" style={{ fontSize: 12, marginBottom: 0 }}>
              <thead>
                <tr>
                  <th style={{ color: '#001f5b' }}>Empreendimento</th>
                  <th style={{ color: '#001f5b' }}>Código</th>
                  <th style={{ color: '#001f5b' }}>QD/LT</th>
                  <th style={{ color: '#001f5b' }}>Vencimento</th>
                  <th style={{ color: '#001f5b', textAlign: 'right' }}>Valor Original</th>
                  <th style={{ color: '#001f5b', textAlign: 'right' }}>Juros/Multa</th>
                  <th style={{ color: '#001f5b', textAlign: 'right' }}>Valor Total</th>
                  <th style={{ color: '#001f5b' }}>Status</th>
                  <th style={{ color: '#001f5b' }}>Chave</th>
                </tr>
              </thead>
              <tbody>
                {carregando ? (
                  <tr>
                    <td colSpan={9} className="text-center py-3">
                      <Spinner animation="border" variant="primary" size="sm" />
                    </td>
                  </tr>
                ) : resultados.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center text-muted py-3">
                      Nenhum débito encontrado.
                    </td>
                  </tr>
                ) : (
                  resultados.map((d) => (
                    <tr key={`${d.codigo}-${d.codigo_ativo}`}>
                      <td>{d.nome_da_familia || '—'}</td>
                      <td>{d.codigo_ativo}</td>
                      <td>{d.quadra_lote || '—'}</td>
                      <td>{fmtData(d.vencimento)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtMoeda(d.valor_original)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtMoeda(d.juros_multa)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtMoeda(d.valor_total)}</td>
                      <td>{d.status || '—'}</td>
                      <td style={{ wordBreak: 'break-all' }}>{d.chave || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            onClick={fecharLimpando}
            className="d-inline-flex align-items-center"
          >
            <FaWindowClose />
            <span className="ml-2">Sair</span>
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal "Dados Complementar para Gerar Chave" — réplica fiel do reuso
          do fExcelGestaoIPTU em uDebitosIPTU.pas:712-794 (caption + 3 opções
          de Despesa + Apuração/Modalidade desabilitados + Tipo Vencimento
          Manual/Boleto; sem seletor de GE, usa o GE do débito). */}
      <ModalDadosChave
        show={mostraModalParametros}
        onHide={() => setMostraModalParametros(false)}
        onConfirmar={handleConfirmarParametros}
        carregando={preparando}
      />

      {/* Modal blocking de avisos — só fecha no clique do OK
          (replica MessageDlg do uDebitosIPTU.pas). */}
      <ModalAvisosIPTU
        show={avisosPendentes.length > 0}
        avisos={avisosPendentes}
        onOk={handleOkAvisos}
      />

      {/* Modal de revisão (segunda tela — RxDBGrid2 do .dfm:603-767). */}
      <ModalRevisaoLancamentoIPTU
        show={mostraModalRevisao}
        onHide={() => {
          setMostraModalRevisao(false);
          setPreparado(null);
        }}
        onLancado={handleLancado}
        token={preparado ? preparado.token : null}
        despesas={preparado ? preparado.despesas : []}
      />
    </>
  );
};

export default ModalDebitosIPTU;
